/**
 * Small, client-side API surface for the deploy flow. Every call goes
 * through here so the page components stay presentational.
 */

export interface DeployResponse {
  slug?: string;
  appUrl?: string;
  error?: string;
}

export interface DeployResult {
  ok: boolean;
  data: DeployResponse;
}

export interface DeployFormFields {
  zip: File;
  name: string;
  description?: string;
  team?: string;
  category?: string;
  icon?: File | null;
  /** Collected secret/env values the user confirmed during the Secret Capture step. */
  secrets?: Array<{ key: string; value: string; isSecret: boolean }>;
}

export interface ScannedEnvVar {
  name: string;
  isSecret: boolean;
  isSecretHint: boolean;
  occurrences: number;
  locations: string[];
  service?: string;
  suggestedDefault?: string;
  description?: string;
}

export interface ScanEnvVarsResponse {
  items: ScannedEnvVar[];
  scanMs: number;
  classifyMs: number;
}

/**
 * Upload the zip to `/api/apps/scan-env-vars` and return the classified
 * env-var list. Used by the Secret Capture step to pre-populate the form.
 */
export async function scanEnvVars(token: string, zip: File): Promise<ScanEnvVarsResponse | null> {
  const formData = new FormData();
  formData.append('zip', zip);
  const response = await fetch('/api/apps/scan-env-vars', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) return null;
  return (await response.json()) as ScanEnvVarsResponse;
}

/**
 * POST a new deploy — zip + metadata, optional icon. Returns the decoded
 * server response and whether the HTTP call succeeded.
 */
export async function postDeploy(token: string, fields: DeployFormFields): Promise<DeployResult> {
  const formData = new FormData();
  formData.append('zip', fields.zip);
  formData.append('name', fields.name);
  formData.append('description', fields.description ?? '');
  formData.append('category', fields.category ?? 'User Apps');
  if (fields.team) formData.append('team', fields.team);
  if (fields.icon) formData.append('icon', fields.icon);
  if (fields.secrets && fields.secrets.length > 0) {
    formData.append('secrets', JSON.stringify(fields.secrets));
  }
  const response = await fetch('/api/apps/deploy', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = (await response.json()) as DeployResponse;
  return { ok: response.ok, data };
}

/**
 * Upload a custom logo for an app and return its public URL. Used by the
 * catalog editor to give manually-registered apps (not just pipeline
 * deploys) a logo. Throws with the server's error text if the upload fails.
 */
export async function uploadAppIcon(token: string, clientId: string, icon: File): Promise<string> {
  const formData = new FormData();
  formData.append('icon', icon);
  formData.append('clientId', clientId);
  let response: Response;
  try {
    response = await fetch('/api/apps/icons', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
      signal: AbortSignal.timeout(20_000),
    });
  } catch (err) {
    const timedOut = err instanceof Error && (err.name === 'TimeoutError' || err.name === 'AbortError');
    if (timedOut) throw new Error('Logo upload timed out');
    throw err;
  }
  const data = (await response.json().catch(() => ({}))) as { iconUrl?: string; error?: string };
  if (!response.ok || !data.iconUrl) {
    throw new Error(data.error || `Logo upload failed (${response.status})`);
  }
  return data.iconUrl;
}

export interface DeployedAppMetaFields {
  name: string;
  description: string;
  category: string;
  team: string;
  icon?: File | null;
}

/**
 * Edit a deployed app's metadata (name, description, category, team, logo)
 * without redeploying. Returns true on success. The backend also syncs these
 * fields onto the app's Keycloak client so the catalog stays consistent.
 */
export async function updateDeployedAppMeta(token: string, slug: string, fields: DeployedAppMetaFields): Promise<boolean> {
  const formData = new FormData();
  formData.append('name', fields.name);
  formData.append('description', fields.description);
  formData.append('category', fields.category);
  formData.append('team', fields.team);
  if (fields.icon) formData.append('icon', fields.icon);
  const response = await fetch(`/api/apps/deployed/${slug}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  return response.ok;
}

/** Fetch the current snapshot of an app's DB row. Returns null on error. */
export async function fetchAppStatus(slug: string, token: string): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetch(`/api/apps/deployed/${slug}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}
